import React, { useState, useEffect, useRef } from 'react';
import {
  RefreshCw, TrendingUp, TrendingDown, DollarSign,
  BarChart3, Download, ChevronDown, Scale
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, LineChart, Line, Cell
} from 'recharts';
import './finance.css';

const BASE_URL = "https://sadaatagrofarms.com";

const fmt = (val) =>
  Number(val || 0).toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Helpers to derive grouped data from flat items ────────────────────────
const groupItems = (items = []) => {
  const oeItems  = items.filter(i => i.item_type === "operation_expense");
  const cwiItems = items.filter(i => i.item_type === "crop_income");
  const opItems  = items.filter(i => i.item_type === "other_profit");

  const sum = (arr) => arr.reduce((acc, i) => acc + Number(i.price || 0), 0);

  return {
    oeItems,
    cwiItems,
    opItems,
    oeTotal:  sum(oeItems),
    cwiTotal: sum(cwiItems),
    opTotal:  sum(opItems),
  };
};

// ─── PDF Generator ─────────────────────────────────────────────────────────
const generateFinancePDF = (detail) => {
  const { oeItems, cwiItems, opItems, oeTotal, cwiTotal, opTotal } = groupItems(detail.items);

  const buildRows = (items) =>
    items.map((item, i) => `
      <tr>
        <td style="text-align:center;color:#6b8070;">${i + 1}</td>
        <td style="font-weight:500;">${item.name || "—"}</td>
        <td style="text-align:right;color:#0f5323;font-weight:600;">Rs ${fmt(item.price)}</td>
      </tr>`).join("");

  const win = window.open("", "_blank");
  win.document.write(`
    <!DOCTYPE html><html>
    <head>
      <meta charset="UTF-8">
      <title>${detail.name} — Finance Report</title>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Source+Sans+3:wght@400;600;700&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Source Sans 3','Segoe UI',sans-serif;background:#f5f7f2;color:#1a2e1e;padding:40px;font-size:14px}
        .cover{background:linear-gradient(135deg,#0f5323 0%,#1a7a35 60%,#2ea84e 100%);border-radius:16px;padding:48px;color:white;margin-bottom:32px;display:flex;justify-content:space-between;align-items:flex-end}
        .cover-title{font-family:'Playfair Display',serif;font-size:36px;line-height:1.1}
        .cover-sub{font-size:13px;opacity:.8;margin-top:6px}
        .cover-meta{text-align:right;font-size:13px;opacity:.85;line-height:1.9}
        .cover-meta strong{display:block;font-family:'Playfair Display',serif;font-size:24px}
        .kpi-row{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:28px}
        .kpi-card{background:white;border-radius:12px;padding:20px;border-left:5px solid;box-shadow:0 2px 12px rgba(0,0,0,.06)}
        .kpi-label{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#6b8070;margin-bottom:6px}
        .kpi-value{font-family:'Playfair Display',serif;font-size:24px;font-weight:700}
        .section-head{font-family:'Playfair Display',serif;font-size:17px;color:#0f5323;padding:11px 15px;border-bottom:2px solid #0f5323;background:rgba(15,83,35,.05);border-radius:8px 8px 0 0;margin-top:28px}
        table{width:100%;border-collapse:collapse;background:white;border-radius:0 0 10px 10px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,.06);margin-bottom:4px}
        thead tr{background:rgba(15,83,35,.08)}
        th{padding:10px 13px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#0f5323;font-weight:700}
        td{padding:10px 13px;border-top:1px solid #e8f0ea;color:#2c4030}
        .total-row td{background:rgba(15,83,35,.08);font-weight:700;border-top:2px solid #0f5323;color:#0f5323}
        .grand-row td{background:linear-gradient(135deg,#0f5323,#1a7a35);color:white;font-weight:800;font-size:15px;padding:14px 13px}
        .watermark{text-align:center;margin-top:48px;font-size:12px;color:#99b8a0;border-top:1px solid #d0e4d6;padding-top:18px}
        @media print{body{background:white;padding:20px}.cover,.kpi-card,.grand-row{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style>
    </head>
    <body>
      <div class="cover">
        <div>
          <div class="cover-title">${detail.name}</div>
          <div class="cover-sub">Financial Summary Report — Sadaat Agro Farms</div>
        </div>
        <div class="cover-meta">
          <strong>Rs ${fmt(detail.net_profit_loss)}</strong>
          Net Profit / Loss<br>
          Generated: ${new Date().toLocaleDateString("en-PK",{year:"numeric",month:"long",day:"numeric"})}
        </div>
      </div>

      <div class="kpi-row">
        <div class="kpi-card" style="border-color:#387eff;">
          <div class="kpi-label">Total Income</div>
          <div class="kpi-value" style="color:#387eff;">Rs ${fmt(detail.total_income)}</div>
        </div>
        <div class="kpi-card" style="border-color:#ff4e4e;">
          <div class="kpi-label">Total Expenses</div>
          <div class="kpi-value" style="color:#ff4e4e;">Rs ${fmt(detail.total_expenses)}</div>
        </div>
        <div class="kpi-card" style="border-color:#00a859;">
          <div class="kpi-label">Total Profit</div>
          <div class="kpi-value" style="color:#00a859;">Rs ${fmt(detail.total_profit)}</div>
        </div>
        <div class="kpi-card" style="border-color:${Number(detail.net_profit_loss)>=0?'#00a859':'#ff4e4e'};">
          <div class="kpi-label">Net Profit / Loss</div>
          <div class="kpi-value" style="color:${Number(detail.net_profit_loss)>=0?'#00a859':'#ff4e4e'};">Rs ${fmt(detail.net_profit_loss)}</div>
        </div>
      </div>

      <!-- Summary Table -->
      <div class="section-head">Financial Summary</div>
      <table>
        <thead><tr><th>Category</th><th style="text-align:right;">Amount (Rs)</th><th style="text-align:right;">% of Income</th></tr></thead>
        <tbody>
          <tr><td>Total Income</td><td style="text-align:right;color:#387eff;font-weight:600;">Rs ${fmt(detail.total_income)}</td><td style="text-align:right;">100%</td></tr>
          <tr><td>Total Expenses</td><td style="text-align:right;color:#ff4e4e;font-weight:600;">Rs ${fmt(detail.total_expenses)}</td><td style="text-align:right;">${Number(detail.total_income)>0?((Number(detail.total_expenses)/Number(detail.total_income))*100).toFixed(1)+"%" :"0%"}</td></tr>
          <tr><td>Total Profit</td><td style="text-align:right;color:#00a859;font-weight:600;">Rs ${fmt(detail.total_profit)}</td><td style="text-align:right;">${Number(detail.total_income)>0?((Number(detail.total_profit)/Number(detail.total_income))*100).toFixed(1)+"%" :"0%"}</td></tr>
          <tr class="grand-row"><td><strong>Net Profit / Loss</strong></td><td style="text-align:right;"><strong>Rs ${fmt(detail.net_profit_loss)}</strong></td><td style="text-align:right;"><strong>${Number(detail.total_income)>0?((Number(detail.net_profit_loss)/Number(detail.total_income))*100).toFixed(1)+"%" :"0%"}</strong></td></tr>
        </tbody>
      </table>

      <!-- Operational Expenses -->
      <div class="section-head">Operational Expenses — Total: Rs ${fmt(oeTotal)}</div>
      <table>
        <thead><tr><th>#</th><th>Name</th><th style="text-align:right;">Amount (Rs)</th></tr></thead>
        <tbody>
          ${buildRows(oeItems)}
          <tr class="total-row"><td colspan="2"><strong>TOTAL</strong></td><td style="text-align:right;"><strong>Rs ${fmt(oeTotal)}</strong></td></tr>
        </tbody>
      </table>

      <!-- Crop-wise Incomes -->
      <div class="section-head">Crop-wise Incomes — Total: Rs ${fmt(cwiTotal)}</div>
      <table>
        <thead><tr><th>#</th><th>Name</th><th style="text-align:right;">Amount (Rs)</th></tr></thead>
        <tbody>
          ${buildRows(cwiItems)}
          <tr class="total-row"><td colspan="2"><strong>TOTAL</strong></td><td style="text-align:right;"><strong>Rs ${fmt(cwiTotal)}</strong></td></tr>
        </tbody>
      </table>

      <!-- Other Profits -->
      <div class="section-head">Other Profits — Total: Rs ${fmt(opTotal)}</div>
      <table>
        <thead><tr><th>#</th><th>Name</th><th style="text-align:right;">Amount (Rs)</th></tr></thead>
        <tbody>
          ${buildRows(opItems)}
          <tr class="total-row"><td colspan="2"><strong>TOTAL</strong></td><td style="text-align:right;"><strong>Rs ${fmt(opTotal)}</strong></td></tr>
        </tbody>
      </table>

      <div class="watermark">Generated automatically by Sadaat Agro Farms Management System &nbsp;•&nbsp; ${new Date().toISOString()}</div>
      <script>window.onload=()=>window.print();<\/script>
    </body></html>
  `);
  win.document.close();
};

// ─── Reusable Items Table ────────────────────────────────────────────────────
const ItemsTable = ({ title, items, total, amountClass, barColor }) => {
  if (!items.length) return null;
  return (
    <>
      <h2 className="section-title">
        {title} — Total: Rs {fmt(total)}
      </h2>
      <div className="chart-container">
        <div className="finance-table-wrapper">
          <table className="finance-table">
            <thead>
              <tr><th>#</th><th>Name</th><th>Amount (Rs)</th><th>% of Total</th><th>Status</th></tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const pct = ((Number(item.price) / (total || 1)) * 100).toFixed(1);
                const avg = total / (items.length || 1);
                const isAbove = Number(item.price) > avg;
                return (
                  <tr key={item.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{item.name}</td>
                    <td className={`amount-cell ${amountClass}`}>Rs {fmt(item.price)}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 7, background: "rgba(15,83,35,0.1)", borderRadius: 4, overflow: "hidden", maxWidth: 90 }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{pct}%</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${isAbove ? (amountClass === "negative" ? "expense" : "income") : (amountClass === "negative" ? "income" : "expense")}`}>
                        {isAbove ? "Above Avg" : "Below Avg"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td colSpan={2}><strong>TOTAL</strong></td>
                <td className={`amount-cell ${amountClass}`}><strong>Rs {fmt(total)}</strong></td>
                <td><strong>100%</strong></td>
                <td><strong>{items.length} items</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const Finance = () => {
  const [summaryList, setSummaryList]   = useState([]);
  const [selectedId, setSelectedId]     = useState(null);
  const [detail, setDetail]             = useState(null);
  const [listLoading, setListLoading]   = useState(false);
  const [loading, setLoading]           = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const COLORS = ["#387eff", "#ff4e4e", "#00a859", "#d80097"];
  const token  = localStorage.getItem("authToken");

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch summary list
  const loadList = () => {
    setListLoading(true);
    fetch(`${BASE_URL}/api/finance/financial-summaries/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setSummaryList(Array.isArray(data) ? data : []); setListLoading(false); })
      .catch(err => { console.error("List error:", err); setListLoading(false); });
  };

  useEffect(() => { loadList(); }, []);

  // Fetch detail when id selected
  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    setDetail(null);
    fetch(`${BASE_URL}/api/finance/financial-summaries/${selectedId}/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setDetail(data); setLoading(false); })
      .catch(err => { console.error("Detail error:", err); setLoading(false); });
  }, [selectedId]);

  const selectedName = summaryList.find(s => s.id === selectedId)?.name;

  // Derived stats & grouped items
  const stats = detail ? {
    income:   Number(detail.total_income),
    expenses: Number(detail.total_expenses),
    profit:   Number(detail.total_profit),
    net:      Number(detail.net_profit_loss)
  } : { income: 0, expenses: 0, profit: 0, net: 0 };

  const { oeItems, cwiItems, opItems, oeTotal, cwiTotal, opTotal } = groupItems(detail?.items);

  const barData = [
    { name: "Income",   value: stats.income   },
    { name: "Expenses", value: stats.expenses  },
    { name: "Profit",   value: stats.profit    },
    { name: "Net P/L",  value: stats.net       }
  ];

  const lineData = [
    { name: "Income",   amount: stats.income   },
    { name: "Expenses", amount: stats.expenses  },
    { name: "Profit",   amount: stats.profit    },
    { name: "Net",      amount: stats.net       }
  ];

  return (
    <div className="finance">
      {dropdownOpen && <div className="budget-dropdown-backdrop" />}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Finance Dashboard</h1>
          <p className="subtitle">Summary of all financial performance</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {detail && (
            <button
              className="refresh-btn"
              style={{ background: "linear-gradient(135deg,#0f5323,#2ea84e)", color: "white", border: "none" }}
              onClick={() => generateFinancePDF(detail)}
            >
              <Download size={16} /> Download PDF
            </button>
          )}
          <button className="refresh-btn" onClick={loadList}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Dropdown */}
      <div style={{ marginBottom: 28, display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ fontWeight: 600, color: "#0f5323", fontSize: 14 }}>Select Summary:</span>
        <div className="budget-custom-dropdown" ref={dropdownRef} style={{ minWidth: 240 }}>
          <div
            className="budget-selected"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <span>{listLoading ? "Loading..." : selectedName ? `${selectedId}. ${selectedName}` : "Select a Financial Summary"}</span>
            <ChevronDown size={16} style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s", marginLeft: 8, flexShrink: 0 }} />
          </div>
          {dropdownOpen && (
            <div className="budget-options">
              <div className="budget-option placeholder" onClick={() => { setSelectedId(null); setDetail(null); setDropdownOpen(false); }}>
                — Select a Summary —
              </div>
              {summaryList.length === 0 && !listLoading && (
                <div className="budget-option placeholder">No summaries found</div>
              )}
              {summaryList.map(s => (
                <div
                  key={s.id}
                  className={`budget-option${selectedId === s.id ? " selected" : ""}`}
                  onClick={() => { setSelectedId(s.id); setDropdownOpen(false); }}
                >
                  {s.id}. {s.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#0f5323", fontSize: 16 }}>
          <RefreshCw size={28} style={{ animation: "spin 1s linear infinite", display: "block", margin: "0 auto 12px" }} />
          Loading financial data…
        </div>
      )}

      {/* Empty state */}
      {!loading && !detail && !selectedId && (
        <div style={{ textAlign: "center", padding: "80px 40px", background: "rgba(15,83,35,0.03)", borderRadius: 16, border: "2px dashed rgba(15,83,35,0.15)" }}>
          <Scale size={48} style={{ color: "#c8ddc0", marginBottom: 16 }} />
          <p style={{ color: "#6b8070", fontSize: 16 }}>Select a financial summary from the dropdown above</p>
        </div>
      )}

      {/* Detail View */}
      {!loading && detail && (
        <>
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon blue"><BarChart3 size={22} /></div>
              <div className="stat-content">
                <div className="stat-value">Rs {stats.income.toLocaleString()}</div>
                <div className="stat-label">Total Income</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon red"><TrendingDown size={22} /></div>
              <div className="stat-content">
                <div className="stat-value">Rs {stats.expenses.toLocaleString()}</div>
                <div className="stat-label">Total Expenses</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon green"><TrendingUp size={22} /></div>
              <div className="stat-content">
                <div className="stat-value">Rs {stats.profit.toLocaleString()}</div>
                <div className="stat-label">Total Profit</div>
              </div>
            </div>
            <div className="stat-card">
              <div className={`stat-icon ${stats.net >= 0 ? "green" : "red"}`}><DollarSign size={22} /></div>
              <div className="stat-content">
                <div className="stat-value">Rs {stats.net.toLocaleString()}</div>
                <div className="stat-label">Net Profit / Loss</div>
              </div>
            </div>
          </div>

          {/* Bar Chart */}
          <h2 className="section-title">Finance Breakdown</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value">
                  {barData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Line Chart */}
          <h2 className="section-title">Overall Summary Trend</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={lineData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="amount" stroke="#4caf50" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Financial Summary Table */}
          <h2 className="section-title">Financial Summary Table</h2>
          <div className="chart-container">
            <div className="finance-table-wrapper">
              <table className="finance-table">
                <thead>
                  <tr><th>Category</th><th>Amount (Rs)</th><th>Percentage</th><th>Status</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="category-cell"><div className="stat-icon blue small-icon"><BarChart3 size={16} /></div><span>Total Income</span></td>
                    <td className="amount-cell positive">Rs {stats.income.toLocaleString()}</td>
                    <td>100%</td>
                    <td><span className="status-badge income">Income</span></td>
                  </tr>
                  <tr>
                    <td className="category-cell"><div className="stat-icon red small-icon"><TrendingDown size={16} /></div><span>Total Expenses</span></td>
                    <td className="amount-cell negative">Rs {stats.expenses.toLocaleString()}</td>
                    <td>{stats.income > 0 ? ((stats.expenses / stats.income) * 100).toFixed(1) + "%" : "0%"}</td>
                    <td><span className="status-badge expense">Expense</span></td>
                  </tr>
                  <tr>
                    <td className="category-cell"><div className="stat-icon green small-icon"><TrendingUp size={16} /></div><span>Total Profit</span></td>
                    <td className="amount-cell positive">Rs {stats.profit.toLocaleString()}</td>
                    <td>{stats.income > 0 ? ((stats.profit / stats.income) * 100).toFixed(1) + "%" : "0%"}</td>
                    <td><span className="status-badge income">Profit</span></td>
                  </tr>
                  <tr className="total-row">
                    <td className="category-cell"><div className="stat-icon green small-icon"><DollarSign size={16} /></div><span><strong>Net Profit / Loss</strong></span></td>
                    <td className={`amount-cell ${stats.net >= 0 ? "positive" : "negative"}`}><strong>Rs {stats.net.toLocaleString()}</strong></td>
                    <td><strong>{stats.income > 0 ? ((stats.net / stats.income) * 100).toFixed(1) + "%" : "0%"}</strong></td>
                    <td><span className={`status-badge ${stats.net >= 0 ? "income" : "expense"}`}>{stats.net >= 0 ? "Profit" : "Loss"}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Operational Expenses */}
          <ItemsTable
            title="Operational Expenses"
            items={oeItems}
            total={oeTotal}
            amountClass="negative"
            barColor="#ff4e4e"
          />

          {/* Crop-wise Incomes */}
          <ItemsTable
            title="Crop-wise Incomes"
            items={cwiItems}
            total={cwiTotal}
            amountClass="positive"
            barColor="#387eff"
          />

          {/* Other Profits */}
          <ItemsTable
            title="Other Profits"
            items={opItems}
            total={opTotal}
            amountClass="positive"
            barColor="#00a859"
          />

          {/* Comparative Analysis Table */}
          <h2 className="section-title">Comparative Analysis</h2>
          <div className="chart-container">
            <div className="finance-table-wrapper">
              <table className="finance-table">
                <thead>
                  <tr><th>Metric</th><th>Value (Rs)</th><th>Difference</th><th>Ratio</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Income vs Expenses</td>
                    <td>Rs {(stats.income - stats.expenses).toLocaleString()}</td>
                    <td className={stats.income > stats.expenses ? "positive" : "negative"}>
                      {stats.income > stats.expenses ? "+" : ""}{((stats.income - stats.expenses) / (stats.income || 1) * 100).toFixed(1)}%
                    </td>
                    <td>{stats.expenses > 0 ? (stats.income / stats.expenses).toFixed(2) : "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Profit Margin</td>
                    <td>Rs {stats.profit.toLocaleString()}</td>
                    <td className={stats.profit >= 0 ? "positive" : "negative"}>
                      {stats.income > 0 ? ((stats.profit / stats.income) * 100).toFixed(1) + "%" : "0%"}
                    </td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Net Performance</td>
                    <td>Rs {stats.net.toLocaleString()}</td>
                    <td className={stats.net >= 0 ? "positive" : "negative"}>
                      {stats.income > 0 ? ((stats.net / stats.income) * 100).toFixed(1) + "%" : "0%"}
                    </td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Operating Efficiency</td>
                    <td>—</td>
                    <td>—</td>
                    <td className={stats.expenses > 0 && (stats.income / stats.expenses) > 1 ? "positive" : "negative"}>
                      {stats.expenses > 0 ? (stats.income / stats.expenses).toFixed(2) + "x" : "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};

export default Finance;